/**
 * Air Map Store — Proprietary Street-Level AQI Data
 *
 * This is the DATA MOAT that makes Blurp.ie defensible against Google.
 *
 * Every runner who uses Blurp.ie contributes GPS-correlated air quality
 * readings that build a hyperlocal air quality map. Over time, this
 * creates street-by-street, block-by-block resolution that fixed
 * monitoring stations can never match.
 *
 * Data is stored locally and could be synced to a backend to create
 * a shared community air quality map.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AQISample } from '@/types/running';

// Grid cell size: ~50m × 50m (0.00045 degrees ≈ 50m at equator)
const GRID_RESOLUTION = 0.00045;

export interface AirMapCell {
  // Grid cell key (rounded lat/lng)
  key: string;
  lat: number;
  lng: number;
  // Aggregated readings
  avgAqi: number;
  avgPm25: number;
  sampleCount: number;
  // Time patterns
  morningAvg: number;   // 5-10am
  middayAvg: number;    // 10am-4pm
  eveningAvg: number;   // 4-9pm
  nightAvg: number;     // 9pm-5am
  morningCount: number;
  middayCount: number;
  eveningCount: number;
  nightCount: number;
  // Metadata
  lastUpdated: number;
  firstSeen: number;
}

interface AirMapState {
  // Grid cells keyed by "lat_lng" rounded to GRID_RESOLUTION
  cells: Record<string, AirMapCell>;
  // Total data points collected
  totalSamples: number;
  // Total unique grid cells mapped
  totalCells: number;
  // Last contribution timestamp
  lastContribution: number | null;

  // Actions
  ingestSamples: (samples: AQISample[]) => void;
  getCellAt: (lat: number, lng: number) => AirMapCell | null;
  getNearbyAverage: (lat: number, lng: number, radiusMeters?: number) => number | null;
  getBestTimeForLocation: (lat: number, lng: number) => string | null;
  getStats: () => { totalSamples: number; totalCells: number; coverageArea: string };
}

function cellKey(lat: number, lng: number): string {
  const roundedLat = Math.round(lat / GRID_RESOLUTION) * GRID_RESOLUTION;
  const roundedLng = Math.round(lng / GRID_RESOLUTION) * GRID_RESOLUTION;
  return `${roundedLat.toFixed(5)}_${roundedLng.toFixed(5)}`;
}

function getTimePeriod(timestamp: string): 'morning' | 'midday' | 'evening' | 'night' {
  const hour = new Date(timestamp).getHours();
  if (hour >= 5 && hour < 10) return 'morning';
  if (hour >= 10 && hour < 16) return 'midday';
  if (hour >= 16 && hour < 21) return 'evening';
  return 'night';
}

export const useAirMapStore = create<AirMapState>()(
  persist(
    (set, get) => ({
      cells: {},
      totalSamples: 0,
      totalCells: 0,
      lastContribution: null,

      /**
       * Ingest AQI samples from a completed run.
       * Each sample gets added to the appropriate grid cell,
       * building up the hyperlocal air quality map over time.
       */
      ingestSamples: (samples: AQISample[]) => {
        if (samples.length === 0) return;

        const cells = { ...get().cells };
        let newCells = 0;

        for (const sample of samples) {
          if (!sample.lat || !sample.lng || sample.aqi <= 0) continue;

          const key = cellKey(sample.lat, sample.lng);
          const period = getTimePeriod(sample.timestamp);
          const pm25 = sample.pm25 ?? sample.aqi * 0.3; // Estimate if not available
          const now = Date.now();

          if (cells[key]) {
            // Update existing cell with running average
            const cell = cells[key];
            const totalAqi = cell.avgAqi * cell.sampleCount + sample.aqi;
            const totalPm25 = cell.avgPm25 * cell.sampleCount + pm25;
            cell.sampleCount += 1;
            cell.avgAqi = totalAqi / cell.sampleCount;
            cell.avgPm25 = totalPm25 / cell.sampleCount;
            cell.lastUpdated = now;

            // Update time-period average
            switch (period) {
              case 'morning': {
                const total = cell.morningAvg * cell.morningCount + sample.aqi;
                cell.morningCount += 1;
                cell.morningAvg = total / cell.morningCount;
                break;
              }
              case 'midday': {
                const total = cell.middayAvg * cell.middayCount + sample.aqi;
                cell.middayCount += 1;
                cell.middayAvg = total / cell.middayCount;
                break;
              }
              case 'evening': {
                const total = cell.eveningAvg * cell.eveningCount + sample.aqi;
                cell.eveningCount += 1;
                cell.eveningAvg = total / cell.eveningCount;
                break;
              }
              case 'night': {
                const total = cell.nightAvg * cell.nightCount + sample.aqi;
                cell.nightCount += 1;
                cell.nightAvg = total / cell.nightCount;
                break;
              }
            }
          } else {
            // Create new cell
            newCells++;
            const initCount = (p: string) => p === period ? 1 : 0;
            const initAvg = (p: string) => p === period ? sample.aqi : 0;

            cells[key] = {
              key,
              lat: Math.round(sample.lat / GRID_RESOLUTION) * GRID_RESOLUTION,
              lng: Math.round(sample.lng / GRID_RESOLUTION) * GRID_RESOLUTION,
              avgAqi: sample.aqi,
              avgPm25: pm25,
              sampleCount: 1,
              morningAvg: initAvg('morning'),
              middayAvg: initAvg('midday'),
              eveningAvg: initAvg('evening'),
              nightAvg: initAvg('night'),
              morningCount: initCount('morning'),
              middayCount: initCount('midday'),
              eveningCount: initCount('evening'),
              nightCount: initCount('night'),
              lastUpdated: now,
              firstSeen: now,
            };
          }
        }

        set({
          cells,
          totalSamples: get().totalSamples + samples.length,
          totalCells: get().totalCells + newCells,
          lastContribution: Date.now(),
        });
      },

      getCellAt: (lat: number, lng: number) => {
        const key = cellKey(lat, lng);
        return get().cells[key] || null;
      },

      getNearbyAverage: (lat: number, lng: number, radiusMeters = 200) => {
        const cells = get().cells;
        const radiusDeg = radiusMeters / 111000; // Rough conversion
        const nearby: number[] = [];

        for (const cell of Object.values(cells)) {
          const dist = Math.sqrt(
            Math.pow(cell.lat - lat, 2) + Math.pow(cell.lng - lng, 2)
          );
          if (dist <= radiusDeg) {
            nearby.push(cell.avgAqi);
          }
        }

        if (nearby.length === 0) return null;
        return nearby.reduce((a, b) => a + b, 0) / nearby.length;
      },

      getBestTimeForLocation: (lat: number, lng: number) => {
        const cell = get().getCellAt(lat, lng);
        if (!cell) return null;

        const periods = [
          { label: 'Morning (5-10am)', avg: cell.morningAvg, count: cell.morningCount },
          { label: 'Midday (10am-4pm)', avg: cell.middayAvg, count: cell.middayCount },
          { label: 'Evening (4-9pm)', avg: cell.eveningAvg, count: cell.eveningCount },
          { label: 'Night (9pm-5am)', avg: cell.nightAvg, count: cell.nightCount },
        ].filter(p => p.count > 0);

        if (periods.length === 0) return null;
        const best = periods.reduce((a, b) => a.avg < b.avg ? a : b);
        return `${best.label} (avg AQI ${Math.round(best.avg)})`;
      },

      getStats: () => {
        const state = get();
        // Rough coverage area: cells × 50m × 50m
        const areaSqM = state.totalCells * 50 * 50;
        const areaKm = areaSqM / 1_000_000;
        return {
          totalSamples: state.totalSamples,
          totalCells: state.totalCells,
          coverageArea: areaKm < 1 ? `${Math.round(areaSqM)} m²` : `${areaKm.toFixed(2)} km²`,
        };
      },
    }),
    {
      name: 'blurp-air-map',
      // Only persist cells and counters
      partialize: (state) => ({
        cells: state.cells,
        totalSamples: state.totalSamples,
        totalCells: state.totalCells,
        lastContribution: state.lastContribution,
      }),
    }
  )
);
